import * as React from 'react';
import {useSearchParams} from 'react-router-dom';

type Options = {
  /**
   * How a filter change enters the browser history.
   *
   * `'replace'` (the default) rewrites the current entry, so filter tweaks
   * don't pile up in the back stack: after filtering a list and opening a
   * detail, one "back" lands on the filtered list instead of stepping
   * backwards through each intermediate filter state.
   *
   * `'push'` records each change instead, making the filters themselves
   * navigable — back returns to the previous filter, forward re-applies the
   * one you left. Worth the longer stack on a list where narrowing is
   * exploratory (Activity), where retracing a step you didn't mean to take
   * otherwise means rebuilding it from the menus.
   */
  history?: 'replace' | 'push';
};

/**
 * URL-query-string-backed filter state, so a list's filters survive
 * navigation: changing a filter rewrites the query string in place, and
 * returning to the list — browser back, or re-mounting the route — restores
 * every filter straight from the URL. Because the query string is also
 * path-scoped, a list rendered per entity (e.g. the account Transactions tab
 * at `/accounts/:id/transactions`) resets to its defaults when the path
 * changes, with no per-entity bookkeeping.
 *
 * `setParams` applies every given key in a single history update, so a control
 * that changes two params at once (e.g. a date range's start + end) can't lose
 * one to a stale-snapshot race. A null or empty value removes the key, keeping
 * the URL clean when a filter returns to its default.
 */
export const useUrlFilterParams = (
  options?: Options,
): [URLSearchParams, (updates: Record<string, string | null>) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const replace = options?.history !== 'push';
  const setParams = React.useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (value == null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      // A no-op update (re-picking the filter already applied) must not push
      // an identical entry: the back button would then appear stuck, undoing
      // nothing visible per press.
      if (replace || next.toString() !== searchParams.toString()) {
        setSearchParams(next, {replace});
      }
    },
    [searchParams, setSearchParams, replace],
  );
  return [searchParams, setParams];
};
